import React, { useState, useRef, useEffect } from 'react';
import { Button, Input, Space, Typography, Select } from 'antd';
import { AsYouType } from 'libphonenumber-js';
import firebase from 'firebase/app';

import {
  COUNTRIES, getPlaceholder, parseE164, nationalFromE164, formatE164ForDisplay, defaultCountry,
} from '../../utils/phone';
import { trackSmsVerificationRequested, trackSmsVerificationCompleted } from '../../utils/smsTracking';

const { Text } = Typography;
const { Option } = Select;

const inputStyle = {
  backgroundColor: '#1a1a1a',
  color: '#fff',
  borderColor: '#444',
};

/**
 * Phone OTP sign-in for clients.
 * Calls onSuccess(authResult) on successful verification.
 * Always sends E.164 to Firebase — never raw user input.
 */
export default function ClientPhoneLogin({
  onSuccess, isSignup, initialPhone, defaultCountryCode,
}) {
  // Carry forward a number already entered (e.g. on the identity-first screen) so the
  // user doesn't retype it. Used only to seed initial state (lazy initializers).
  const prefill = initialPhone ? nationalFromE164(initialPhone) : null;
  const [step, setStep] = useState('enter');
  const [country, setCountry] = useState(
    () => (prefill && COUNTRIES.find((c) => c.code === prefill.country)) || defaultCountry(defaultCountryCode),
  );
  const [rawInput, setRawInput] = useState(() => (prefill ? prefill.national : ''));
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const confirmationRef = useRef(null);
  const recaptchaContainerRef = useRef(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Re-format as user types using AsYouType
  const handleRawChange = (e) => {
    const formatter = new AsYouType(country.code);
    setRawInput(formatter.input(e.target.value));
    setError(null);
  };

  const e164 = parseE164(rawInput, country.code);
  const isValid = !!e164;

  const handleSendCode = async () => {
    if (!e164) return;
    setError(null);
    setLoading(true);
    try {
      const container = recaptchaContainerRef.current;
      if (!container) throw new Error('reCAPTCHA container not ready');
      container.innerHTML = '';
      const verifier = new firebase.auth.RecaptchaVerifier(container, { size: 'normal' });
      const result = await firebase.auth().signInWithPhoneNumber(e164, verifier);
      confirmationRef.current = result;
      trackSmsVerificationRequested({ e164, inputCountry: country.code, context: 'login' });
      setStep('verify');
      setResendCooldown(60);
    } catch (err) {
      setError(err.message || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6 || !confirmationRef.current) return;
    setError(null);
    setLoading(true);
    try {
      const authResult = await confirmationRef.current.confirm(code.trim());
      trackSmsVerificationCompleted({ e164, inputCountry: country.code, context: 'login' });
      onSuccess(authResult, { countryCode: country.code });
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep('enter');
    setCode('');
    setError(null);
    setRawInput('');
    confirmationRef.current = null;
  };

  const handleResend = () => {
    setStep('enter');
    setCode('');
    setError(null);
    confirmationRef.current = null;
    // rawInput kept so user can tap "Send verification code" without retyping
  };

  return (
    <div style={{ marginTop: 8 }}>
      {step === 'enter' ? (
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Input.Group compact style={{ display: 'flex' }}>
            <Select
              value={country.code}
              onChange={(code) => {
                setCountry(COUNTRIES.find((c) => c.code === code) || COUNTRIES[0]);
                setRawInput(''); // user changed country → clear the number
                setError(null);
              }}
              size="large"
              style={{ width: 120, flexShrink: 0 }}
              disabled={loading}
              dropdownMatchSelectWidth={false}
            >
              {COUNTRIES.map((c) => (
                <Option key={c.code} value={c.code}>
                  {c.flag} {c.dial}
                </Option>
              ))}
            </Select>
            <Input
              placeholder={getPlaceholder(country.code)}
              value={rawInput}
              onChange={handleRawChange}
              size="large"
              style={{ ...inputStyle, flex: 1 }}
              disabled={loading}
              autoComplete="tel-national"
              inputMode="tel"
            />
          </Input.Group>
          {isValid && (
            <Text style={{ color: '#52c41a', fontSize: 12 }}>✓ {formatE164ForDisplay(e164)}</Text>
          )}
          <div
            ref={recaptchaContainerRef}
            style={{ display: 'flex', justifyContent: 'center', margin: '4px 0' }}
          />
          <Button
            type="primary"
            block
            size="large"
            onClick={handleSendCode}
            disabled={!isValid || loading}
            loading={loading}
            style={{ backgroundColor: '#ff0098', borderColor: '#ff0098' }}
          >
            Send verification code
          </Button>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Text style={{ color: '#aaa', fontSize: 13 }}>
            Code sent to {formatE164ForDisplay(e164)}
          </Text>
          <Input
            placeholder="123456"
            value={code}
            onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(null); }}
            size="large"
            maxLength={6}
            style={{ ...inputStyle, textAlign: 'center', letterSpacing: 6, fontSize: 20 }}
            disabled={loading}
            autoComplete="one-time-code"
          />
          <Button
            type="primary"
            block
            size="large"
            onClick={handleVerify}
            disabled={code.length !== 6 || loading}
            loading={loading}
            style={{ backgroundColor: '#ff0098', borderColor: '#ff0098' }}
          >
            {isSignup ? 'Verify & create account' : 'Verify & sign in'}
          </Button>
          <Button
            type="text"
            block
            onClick={handleResend}
            disabled={resendCooldown > 0}
            style={{ color: resendCooldown > 0 ? '#555' : '#888' }}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
          </Button>
          <Button type="text" block onClick={handleReset} style={{ color: '#666', fontSize: 12 }}>
            Use a different number
          </Button>
        </Space>
      )}
      {error && (
        <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
          {error}
        </Text>
      )}
    </div>
  );
}
